import {
  CREDIT_COSTS_MATRIX,
  isActionAvailable,
  isMatrixAction,
  type BillableActionType,
  type CompanySize,
  type Incadrare,
  type NivelRisc,
} from "@/constants/credit";

export type PricingStage = "S1" | "S2" | "S3" | "S4";
export type PricingDimension = "MI" | "MJ" | "MA";
export type PricingNis2Class = "Imp" | "Es";
export type PricingRiskLevel = "B" | "I" | "E";
export type PricingServiceType = "online" | "download" | "ai" | "b2b";

export type PricingService = {
  id: number;
  stage: PricingStage;
  name: string;
  short: string;
  long: string;
  type: PricingServiceType;
  actionType: BillableActionType | null;
  free: boolean;
  isB2B?: boolean;
  flagship?: boolean;
  draft?: boolean;
  comingSoon?: boolean;
};

const DIM_TO_SIZE: Record<PricingDimension, CompanySize> = {
  MI: "S",
  MJ: "M",
  MA: "L",
};

const NIS2_TO_INCADRARE: Record<PricingNis2Class, Incadrare> = {
  Imp: "IMPORTANTA",
  Es: "ESENTIALA",
};

const RISK_TO_NIVEL: Record<PricingRiskLevel, NivelRisc> = {
  B: "B",
  I: "I",
  E: "E",
};

export const dimLabels: Record<PricingDimension, string> = {
  MI: "Micro / Mică",
  MJ: "Mijlocie",
  MA: "Mare",
};

export const nis2Labels: Record<PricingNis2Class, string> = {
  Imp: "Importantă",
  Es: "Esențială",
};

export const riskLabels: Record<PricingRiskLevel, string> = {
  B: "Bază",
  I: "Important",
  E: "Esențial",
};

export const stageLabels: Record<PricingStage, string> = {
  S1: "Identificare & Înregistrare",
  S2: "Analiza de risc ENIRE",
  S3: "Evaluare maturitate CyFun",
  S4: "Plan Remediere & Guvernanță",
};

export const services: PricingService[] = [
  {
    id: 1,
    stage: "S1",
    name: "Notificare inițială DNSC – Workflow online",
    short:
      "Aflați în 5 minute dacă firma intră sub NIS2 și cum este încadrată.",
    long: "Workflow ghidat online care aplică algoritmul oficial DNSC pentru calcularea dimensiunii companiei și identificarea încadrării entității conform NIS2 și OUG 155/2024.",
    type: "online",
    actionType: "notificare_initiala_dnsc",
    free: true,
  },
  {
    id: 2,
    stage: "S1",
    name: "Analiza efectului perturbator (Art. 9)",
    short: "Evaluați dacă serviciile firmei au efect perturbator semnificativ.",
    long: "Instrument de evaluare a efectului perturbator, aplicabil entităților care nu se încadrează automat pe baza Art. 6–8. Platforma ghidează prin criteriile Ghidului DNSC.",
    type: "online",
    actionType: "analiza_efect_perturbator",
    free: true,
  },
  {
    id: 3,
    stage: "S1",
    name: "PDF Analiza efectului perturbator",
    short: "PDF-ul oficial, gata de semnat și trimis la DNSC.",
    long: "Generare automată a formularului oficial conform template-ului DNSC. Include răspunsurile, punctajele și justificările.",
    type: "download",
    actionType: "doc_analiza_efect_perturbator_pdf",
    free: false,
  },
  {
    id: 4,
    stage: "S1",
    name: "PDF Notificare DNSC (semnabil)",
    short:
      "Formularul oficial de notificare, pre-completat – fără efort manual.",
    long: "Generare automată a formularului oficial de notificare DNSC. Respectă exact structura impusă de DNSC. PDF gata de semnătură electronică calificată.",
    type: "download",
    actionType: "doc_notificare_dnsc_pdf",
    free: false,
  },
  {
    id: 5,
    stage: "S1",
    name: "Evidență Decizie de înregistrare DNSC",
    short:
      "Stocare digitală a Deciziei DNSC – fundament pentru obligațiile ulterioare.",
    long: "Modul de evidență digitală a deciziei oficiale primite de la DNSC. Sistemul arhivează decizia și o folosește pentru a parametriza workflow-urile ulterioare.",
    type: "online",
    actionType: "evidenta_decizie_inregistrare_dnsc",
    free: true,
  },
  {
    id: 6,
    stage: "S2",
    name: "Evaluare de risc ENIRE (Analiza sectorială)",
    short:
      "Parcurgeți online analiza ENIRE oficială și aflați profilul de risc CyFun.",
    long: "Interfață online pentru instrumentul oficial ENIRE al DNSC. Workflow-ul preia sectoarele din Decizia DNSC și calculează automat profilul de risc (Bază / Important / Esențial).",
    type: "online",
    actionType: "evaluare_risc_enire",
    free: true,
  },
  {
    id: 7,
    stage: "S2",
    name: "Justificare personalizată parametri ENIRE",
    short:
      "Serviciu de consultanță B2B – facturat separat, pe bază de contract.",
    long: "Pentru entitățile care doresc să modifice valorile implicite din ENIRE. Conform Ghidului DNSC, orice abatere necesită justificare tehnică. Serviciul este furnizat în afara platformei.",
    type: "b2b",
    actionType: null,
    free: false,
    isB2B: true,
  },
  {
    id: 8,
    stage: "S2",
    name: "PDF Analiza de risc ENIRE",
    short: "PDF-ul oficial al analizei de risc, gata pentru arhivare și audit.",
    long: "Generare automată a documentului oficial ENIRE în format PDF, cu scorurile și profilul de risc. Respectă structura instrumentului ENIRE v2.3.",
    type: "download",
    actionType: "doc_analiza_risc_enire_pdf",
    free: false,
  },
  {
    id: 9,
    stage: "S3",
    name: "Evaluare preliminară maturitate CyFun",
    short: "În 20 de minute aflați unde vă aflați față de cerințele CyFun.",
    long: "Chestionar de auto-evaluare preliminară care identifică rapid politicile, procedurile și controalele implementate. Diferențiat – Bază ≈ 30 controale vs Esențial ≈ 90+.",
    type: "online",
    actionType: "chestionar_preliminar_v2",
    free: true,
    draft: true,
  },
  {
    id: 10,
    stage: "S3",
    name: "Evaluare completă maturitate CyFun (autoghidată)",
    short: "Evaluare CyFun completă control-cu-control – gata de audit.",
    long: "Workflow complet cu parcurgere control-cu-control pe 5 niveluri de maturitate cu exemple de dovezi per nivel. O entitate Mare-Esențială evaluează ~5× mai multe controale.",
    type: "online",
    actionType: "calcul_maturitate_v2",
    free: true,
    draft: true,
  },
  {
    id: 11,
    stage: "S3",
    name: "Evaluare asistată AI a maturității CyFun",
    short: "AI-ul pre-completează evaluarea CyFun din documentele firmei.",
    long: "Agent AI care parsează documente (politici, proceduri, jurnale), le corelează cu fiecare control CyFun și pre-completează evaluarea. Reviziunea durează ore, nu săptămâni.",
    type: "ai",
    actionType: "eval_maturitate_ai_asistata",
    free: false,
    comingSoon: true,
  },
  {
    id: 12,
    stage: "S3",
    name: "Raport oficial maturitate (Excel + PDF)",
    short: "Raportul final CyFun – documentul oficial pentru audit.",
    long: "Raportul final în Excel (analiză pe controale) și PDF (semnătură). Include scoruri per control, dovezi, comentarii și reprezentări grafice.",
    type: "download",
    actionType: "s3_raport_pdf_v2",
    free: false,
  },
  {
    id: 13,
    stage: "S4",
    name: "Plan de remediere NIS2 (Construire asistată)",
    short: "Gap-urile CyFun transformate într-un plan concret, prioritizat.",
    long: "Workflow ghidat care propune automat acțiuni de remediere, resurse, prioritizare pe bază de risc și termene. Echivalent consultanță €3–8k pe piață.",
    type: "online",
    actionType: "plan_remediere_v2",
    free: false,
    flagship: true,
    comingSoon: true,
  },
  {
    id: 14,
    stage: "S4",
    name: "PDF Plan de remediere (DOCX + PDF)",
    short: "Plan finalizat, gata de aprobare și prezentare la audit.",
    long: "Documentul oficial în DOCX (editabil) și PDF (semnabil). Include acțiunile, termenele, responsabilii, resursele și prioritizarea.",
    type: "download",
    actionType: "doc_plan_remediere_pdf",
    free: false,
    comingSoon: true,
  },
  {
    id: 15,
    stage: "S4",
    name: "Stabilire apetit la risc (Framework NIS2)",
    short: "Declarația oficială de Apetit la Risc – obligatoriu la audit.",
    long: "Workflow de stabilire formală a apetitului la risc: active critice, niveluri de toleranță, praguri de escaladare. Echivalent consultanță €2–5k pe piață.",
    type: "online",
    actionType: "stabilire_apetit_risc",
    free: false,
    flagship: true,
    comingSoon: true,
  },
  {
    id: 16,
    stage: "S4",
    name: "Declarația de apetit la risc (DOCX + PDF)",
    short: "Declarația oficială, gata de aprobat de conducere.",
    long: "DOCX (editare finală management) și PDF (semnabil). Respectă ISO 27005 și cerințele NIS2. Destinat aprobării la nivel de CA.",
    type: "download",
    actionType: "doc_apetit_risc_pdf",
    free: false,
    comingSoon: true,
  },
];

/**
 * Returns the credit cost for a service at a given (size, încadrare, risc)
 * profile. Returns null when the service is B2B (no credit cost) or when the
 * action is unavailable for the profile (e.g. plan_remediere for IMPORTANTA).
 */
export function getServiceCredits(
  service: PricingService,
  dim: PricingDimension,
  nis2: PricingNis2Class,
  risk: PricingRiskLevel
): number | null {
  if (!service.actionType) return null;
  const ctx = {
    size: DIM_TO_SIZE[dim],
    incadrare: NIS2_TO_INCADRARE[nis2],
    nivelRisc: RISK_TO_NIVEL[risk],
  };
  if (!isActionAvailable(service.actionType, ctx)) return null;
  if (!isMatrixAction(service.actionType)) return null;
  return CREDIT_COSTS_MATRIX[service.actionType][ctx.size][ctx.incadrare][
    ctx.nivelRisc
  ];
}

/**
 * Returns a "min–max cr" range label across all defined cells of the action's
 * matrix. For B2B returns "B2B".
 */
export function getCreditsRangeLabel(service: PricingService): string {
  if (service.isB2B || !service.actionType) return "B2B";
  if (!isMatrixAction(service.actionType)) return "—";

  const matrix = CREDIT_COSTS_MATRIX[service.actionType];
  let min = Infinity;
  let max = -Infinity;
  for (const size of ["S", "M", "L"] as const) {
    for (const inc of ["IMPORTANTA", "ESENTIALA"] as const) {
      for (const nr of ["B", "I", "E"] as const) {
        const v = matrix[size][inc][nr];
        if (v === null) continue;
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }
  if (!isFinite(min)) return "—";
  if (min === max) return `${min} cr`;
  return `${min}–${max} cr`;
}
